// src/context.ts
var FEATURE_FLAG_ENVIRONMENTS = {
  DEVELOPMENT: "development",
  STAGING: "staging",
  PRODUCTION: "production"
};
var VERCEL_ENVIRONMENTS = {
  DEVELOPMENT: FEATURE_FLAG_ENVIRONMENTS.DEVELOPMENT,
  PREVIEW: "preview",
  PRODUCTION: FEATURE_FLAG_ENVIRONMENTS.PRODUCTION
};
var compactProperties = (values) => Object.fromEntries(
  Object.entries(values).filter(
    (entry) => entry[1] !== null && entry[1] !== void 0 && entry[1] !== ""
  )
);
var toFeatureFlagEnvironment = (mode, deploymentEnvironment, override) => {
  if (override === FEATURE_FLAG_ENVIRONMENTS.DEVELOPMENT || override === FEATURE_FLAG_ENVIRONMENTS.STAGING || override === FEATURE_FLAG_ENVIRONMENTS.PRODUCTION) {
    return override;
  }
  if (deploymentEnvironment === VERCEL_ENVIRONMENTS.PREVIEW) {
    return FEATURE_FLAG_ENVIRONMENTS.STAGING;
  }
  if (deploymentEnvironment === VERCEL_ENVIRONMENTS.PRODUCTION) {
    return FEATURE_FLAG_ENVIRONMENTS.PRODUCTION;
  }
  if (deploymentEnvironment === VERCEL_ENVIRONMENTS.DEVELOPMENT) {
    return FEATURE_FLAG_ENVIRONMENTS.DEVELOPMENT;
  }
  if (mode === FEATURE_FLAG_ENVIRONMENTS.PRODUCTION) {
    return FEATURE_FLAG_ENVIRONMENTS.PRODUCTION;
  }
  if (mode === FEATURE_FLAG_ENVIRONMENTS.STAGING) {
    return FEATURE_FLAG_ENVIRONMENTS.STAGING;
  }
  return FEATURE_FLAG_ENVIRONMENTS.DEVELOPMENT;
};
var buildPosthogGroupProperties = (context) => compactProperties({
  app: context.app,
  environment: context.environment,
  shop_id: context.shop.id,
  shop_domain: context.shop.domain,
  plan_name: context.shop.plan,
  country_code: context.shop.country,
  partner_development: context.shop.partnerDevelopment,
  subscription_created_at: context.shop.subscriptionCreatedAt
});
var buildPosthogPersonProperties = (context) => compactProperties({
  app: context.app,
  environment: context.environment,
  shop_id: context.shop.id,
  shop_domain: context.shop.domain,
  subscription_created_at: context.shop.subscriptionCreatedAt
});

// src/flags.ts
var FEATURE_FLAGS = {
  REELPIK: {
    BACKGROUND_VIDEO_UPLOAD: "background_video_upload_enabled",
    BUY_NOW: "buy_now_enabled",
    PRODUCT_PAGE_VIDEOS: "product_page_videos_enabled"
  },
  INVOICE: {
    APP_NEW_DESIGN: "app_invoice_new_design",
    HELP_CENTER: "help_center",
    SHOPIFY_FLOW: "shopify_flow",
    GUIDED_VIDEOS: "videos_guideline",
    EINVOICE_XML_EXPORT_UI: "einvoice_xml_export_ui",
    SUBSCRIPTION_DOWNGRADE_FLOW: "new_subscription_downgrade_flow",
    NEW_ONBOARDING: "new_onboarding",
    THEME_EDIT_CODE: "theme_edit_code",
    SHOW_ORDER_TEMPLATE: "show_order_template",
    ENABLE_NEW_ORDER_DETAIL: "enable_new_order_detail",
    VIRTUAL_TOUR: "virtual_tour"
  }
};

// src/overrides.ts
import posthog from "posthog-js";
var overrideFeatureFlags = (flags) => {
  posthog.featureFlags.overrideFeatureFlags({ flags });
};

// src/react.tsx
import posthog2 from "posthog-js";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { jsx } from "react/jsx-runtime";
var FeatureFlagRuntimeContext = createContext(
  null
);
var FeatureFlagProvider = ({
  children,
  ready,
  error = false
}) => {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!ready || typeof posthog2.onFeatureFlags !== "function") return;
    let unsubscribe;
    try {
      unsubscribe = posthog2.onFeatureFlags(() => {
        setRevision((current) => current + 1);
      });
    } catch (error2) {
      console.warn("[FeatureFlags] Failed to subscribe to flag updates", error2);
    }
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [ready]);
  const value = useMemo(
    () => ({ ready: ready && !error, revision }),
    [ready, error, revision]
  );
  return /* @__PURE__ */ jsx(FeatureFlagRuntimeContext.Provider, { value, children });
};
var useFeatureFlagRuntime = () => {
  const runtime = useContext(FeatureFlagRuntimeContext);
  if (!runtime) {
    throw new Error("useFeatureFlag must be used within FeatureFlagProvider");
  }
  return runtime;
};
var useFeatureFlagValue = (flag) => {
  var _a, _b;
  const runtime = useFeatureFlagRuntime();
  if (!runtime.ready) return false;
  return ((_b = (_a = posthog2).getFeatureFlag) == null ? void 0 : _b.call(_a, flag, { send_event: false })) ?? false;
};
var useLegacyFeatureFlag = (flag) => {
  var _a, _b;
  const runtime = useFeatureFlagRuntime();
  if (!runtime.ready) return false;
  return ((_b = (_a = posthog2).isFeatureEnabled) == null ? void 0 : _b.call(_a, flag, { send_event: false })) === true;
};
var useFeatureFlag = (flag) => useLegacyFeatureFlag(flag);
var FeatureGate = ({
  flag,
  children,
  fallback = null
}) => useFeatureFlag(flag) ? children : fallback;
export {
  FEATURE_FLAGS,
  FEATURE_FLAG_ENVIRONMENTS,
  FeatureFlagProvider,
  FeatureGate,
  VERCEL_ENVIRONMENTS,
  buildPosthogGroupProperties,
  buildPosthogPersonProperties,
  overrideFeatureFlags,
  toFeatureFlagEnvironment,
  useFeatureFlag,
  useFeatureFlagValue,
  useLegacyFeatureFlag
};
